import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';
import { AvatarList } from '@organisms/dataDisplay';
import { LoadingButton } from '@atoms/buttons';
import { Box, Flex } from '@atoms/layout';
import { FaIcon } from '@atoms/icons';
import { Stack } from '@mui/material';

type AssigneeButton = {
  value: {
    id: string;
    avatar?: string;
    name: string;
    assignedByClient?: boolean;
  }[];
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  label?: ReactNode;
  onAssignClick?: () => void;
  onUnassignClick?: () => void;
  tooltip?: ReactNode;
  alignAssigneeButtonVariant?: 'left' | 'center' | 'right';
};
export function AssigneeButton({
  fullWidth,
  loading,
  onAssignClick,
  onUnassignClick,
  value,
  disabled,
  label,
  tooltip,
  alignAssigneeButtonVariant,
}: AssigneeButton) {
  const theme = useTheme();
  const openUnassign = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    onUnassignClick?.();
  };

  if (value.length === 0) {
    return (
      <Stack direction="row" width="100%" justifyContent={alignAssigneeButtonVariant} alignItems="center">
        <Tooltip title={tooltip}>
          <span>
            <LoadingButton
              id="assign"
              onClick={(event) => {
                event.stopPropagation();
                onAssignClick?.();
              }}
              loading={loading}
              rounded
              disabled={disabled}
              fullWidth={fullWidth}
            >
              <Fade in={!loading} timeout={150}>
                <Flex alignCenter>
                  <Box fontSize={16} mr={1} ml={1}>
                    <FaIcon
                      name="user-magnifying-glass"
                      color={disabled ? theme.palette.action.disabled : theme.palette.primary.main}
                    />
                  </Box>
                  {label ?? <FormattedMessage id="shifts.assignBtnLabel" />}
                </Flex>
              </Fade>
            </LoadingButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Flex justifyContent="space-between" alignCenter height="100%" pl={1}>
      <Fade in={!loading} timeout={150}>
        <Flex
          alignCenter
          gap={1.75}
          sx={{
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <AvatarList
            list={value.map((v) => ({
              id: v.id,
              avatar: v.avatar,
              name: v.name,
            }))}
          />
        </Flex>
      </Fade>

      <Tooltip title={disabled ? <FormattedMessage id="shifts.unassignButton.disabledTooltip" /> : ''} placement="left">
        <span>
          <Fade in={!loading} timeout={150}>
            <IconButton sx={{ width: '40px' }} onClick={openUnassign} disabled={disabled}>
              <FaIcon name="xmark" variant="light" color="inherit" />
            </IconButton>
          </Fade>
        </span>
      </Tooltip>
    </Flex>
  );
}
